"use client";

import type { ChatRequestOptions, Message } from "ai";
import { useChat } from "ai/react";

import { useState, useRef, useEffect, FormEvent, useMemo } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
// import Image from "next/image";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { IntermediateStep } from "./IntermediateStep";
import { Button } from "../ui/button";
import { UploadDocumentsForm } from "./UploadDocumentsForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { ArrowDown, Paperclip, Sparkles, Waves, WrenchIcon } from "lucide-react";
import { Typewriter } from "../ui/typewriter-text";
import { TextShimmer } from "../ui/text-shimmer";
import { VercelV0Chat } from "../ui/v0-ai-chat";
import { MCPToolListItem } from "../lib/types/mcp";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { TextGenerateEffect } from "./TextGenerateEffect";
import { FlipWords } from "../ui/flip-words";
// import { useRouter } from "next/navigation";

// Extended Message type to include tool_calls property and 'tool' role
type ExtendedRole = 'user' | 'assistant' | 'system' | 'tool' | 'data';

interface ExtendedMessage {
  id: string;
  content: string;
  role: ExtendedRole;
  tool_calls?: Array<{
    id?: string;
    type?: string;
    function?: {
      name?: string;
      arguments?: string;
    };
    name?: string;
    input?: unknown;
  }>;
  createdAt?: Date;
  annotations?: any[];
}

// New type for stored MCP servers
interface StoredMCPServer {
  url: string;
  name?: string;
  addedAt: number;
}

// Extended properties for MCP tools
interface MCPToolExtended extends MCPToolListItem {
  serverUrl?: string; // URL of the MCP server this tool belongs to
  serverId?: string; // ID of the MCP server this tool belongs to
  config?: any; // Config of the MCP server this tool belongs to
  parameters?: any; // Tool parameters structure for execution
  type?: string; // Tool type (function, etc.)
  function?: any; // Function details for function-type tools
}

function ChatMessages(props: {
  messages: Message[];
  emptyStateComponent: ReactNode;
  sourcesForMessages: Record<string, any>;
  aiEmoji?: string;
  className?: string;
  isLoading: boolean;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom();
    }
  }, [props.messages, isAtBottom]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const atBottom = scrollHeight - scrollTop - clientHeight < 10;
      setIsAtBottom(atBottom);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  if (props.messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        {props.emptyStateComponent}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-2 sm:px-4 py-6 scroll-smooth"
    >
      <div className="max-w-4xl mx-auto">
        <AnimatePresence initial={false}>
          {props.messages.map((m, i) => {
            if (m.role === "system") {
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <IntermediateStep message={m} />
                </motion.div>
              );
            }

            const sourceKey = (props.messages.length - 1 - i).toString();
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChatMessageBubble
                  message={m}
                  aiEmoji={props.aiEmoji}
                  sources={props.sourcesForMessages[sourceKey]}
                />
              </motion.div>
            );
          })}

          {props.isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 rounded-lg bg-muted/50 w-fit max-w-[80%] ml-4 mt-4"
            >
              <TextShimmer
                duration={1.2}
                className="text-sm font-medium"
              >
                AI is thinking...
              </TextShimmer>{" "}
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {!isAtBottom && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-10"
        >
          <Button
            variant="secondary"
            className="rounded-full shadow-lg flex gap-2 items-center px-4"
            onClick={scrollToBottom}
          >
            <ArrowDown className="w-4 h-4" />
            <span>New messages</span>
          </Button>
        </motion.div>
      )}
    </div>
  );
}

function MCPUrlDialog({ open, onOpenChange, onSubmit }: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onSubmit: (urls: string[]) => void;
}) {
  const [url, setUrl] = useState('');
  const [savedServers, setSavedServers] = useState<StoredMCPServer[]>([]);
  const [selectedServers, setSelectedServers] = useState<string[]>([]);

  // Load saved servers on mount
  useEffect(() => {
    if (open) {
      try {
        const stored = localStorage.getItem('mcpServers');
        if (stored) {
          const servers = JSON.parse(stored) as StoredMCPServer[];
          setSavedServers(servers);
        }
      } catch (e) {
        console.error('Error loading saved MCP servers:', e);
      }
    }
  }, [open]);

  const handleToggleServer = (serverUrl: string) => {
    setSelectedServers(prev => 
      prev.includes(serverUrl) 
        ? prev.filter(url => url !== serverUrl)
        : [...prev, serverUrl]
    );
  };

  const handleAddServer = () => {
    if (!url.trim()) return;
    
    try {
      // Validate URL
      new URL(url.trim());
      
      // Add to saved servers
      const newServer: StoredMCPServer = {
        url: url.trim(),
        name: new URL(url.trim()).hostname,
        addedAt: Date.now()
      };
      
      const updatedServers = [...savedServers, newServer];
      setSavedServers(updatedServers);
      setSelectedServers(prev => [...prev, newServer.url]);
      
      // Save to localStorage
      localStorage.setItem('mcpServers', JSON.stringify(updatedServers));
      
      // Clear input
      setUrl('');
    } catch (e) {
      toast.error('Invalid URL format');
    }
  };

  const handleRemoveServer = (serverUrl: string) => {
    const updatedServers = savedServers.filter(s => s.url !== serverUrl);
    setSavedServers(updatedServers);
    setSelectedServers(prev => prev.filter(url => url !== serverUrl));
    
    // Update localStorage
    localStorage.setItem('mcpServers', JSON.stringify(updatedServers));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    // Add current URL if not empty
    if (url.trim()) {
      handleAddServer();
    }
    
    // Submit selected servers
    if (selectedServers.length > 0) {
      onSubmit(selectedServers);
      onOpenChange(false);
    } else {
      toast.error('Please select at least one MCP server');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Connect to MCP Servers</DialogTitle>
            <DialogDescription>
              Enter MCP server URLs or select from previously saved servers
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mcpUrl">Add MCP Server URL</Label>
              <div className="flex gap-2">
                <Input
                  id="mcpUrl"
                  placeholder="https://mcp.composio.dev/composio/server/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={handleAddServer}
                >
                  Add
                </Button>
              </div>
            </div>
            
            {savedServers.length > 0 && (
              <div className="space-y-2">
                <Label>Saved MCP Servers</Label>
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-[200px] overflow-y-auto">
                    {savedServers.map((server) => (
                      <div 
                        key={server.url} 
                        className="flex items-center justify-between p-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`server-${server.url}`}
                            checked={selectedServers.includes(server.url)}
                            onChange={() => handleToggleServer(server.url)}
                            className="rounded"
                          />
                          <Label 
                            htmlFor={`server-${server.url}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {server.name || new URL(server.url).hostname}
                          </Label>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveServer(server.url)}
                          className="h-6 w-6 p-0"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Connect</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ChatWindow(props: {
  endpoint: string;
  emptyStateComponent: ReactNode;
  placeholder?: string;
  emoji?: string;
  showIngestForm?: boolean;
  showIntermediateStepsToggle?: boolean;
  initialMessages?: Message[];
  conversationId?: string;
  onConversationCreated?: (conversationId: string) => void;
  onMessageFinished?: () => void;
  // Base URL for API calls
  baseUrl?: string;
  // Styling
  inputClassName?: string;
  buttonsClassName?: string;
  dropdownClassName?: string;
  dropdownDirection?: 'top' | 'bottom';
  // CORS handling
  useProxy?: boolean;
  proxyUrl?: string;
  corsHeaders?: Record<string, string>;
  // Auth context
  organizationId?: string;
  publicScope?: boolean;
  isAuthenticated?: boolean;
}) {
  const [sourcesForMessages, setSourcesForMessages] = useState<
    Record<string, any>
  >({});
  const [showIntermediateSteps, setShowIntermediateSteps] = useState(
    !!props.showIntermediateStepsToggle
  );
  const [intermediateStepsLoading, setIntermediateStepsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedMCPTool, setSelectedMCPTool] = useState<MCPToolExtended | null>(null);
  const [selectedKnowledgeSources, setSelectedKnowledgeSources] = useState<string[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(props.conversationId);
  // const router = useRouter();

  const chat = useChat({
    api: props.endpoint,
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      show_suggestions: showSuggestions,
      conversationId: conversationId,
      organizationId: props.organizationId,
      publicScope: props.publicScope,
      isAuthenticated: props.isAuthenticated,
    },
    onToolCall: ({ toolCall }) => {
      // Tool call handling
      try {
        const storedTools = localStorage.getItem('selectedMCPTools');
        if (storedTools) {
          const tools = JSON.parse(storedTools);
        }
      } catch (e) {
        console.error('Error reading stored tools:', e);
      }
    },
    onResponse(response) {
      // Capture conversation ID from headers
      const conversationIdFromHeader = response.headers.get("x-conversation-id") || 
                                      response.headers.get("X-Conversation-Id");
      if (conversationIdFromHeader && props.onConversationCreated) {
        props.onConversationCreated(conversationIdFromHeader);
      }
      
      const sourcesHeader = response.headers.get("x-sources");
      const sources = sourcesHeader
        ? JSON.parse(Buffer.from(sourcesHeader, "base64").toString("utf8"))
        : [];

      const messageIndexHeader = response.headers.get("x-message-index");
      if (sources.length && messageIndexHeader !== null) {
        setSourcesForMessages({
          ...sourcesForMessages,
          [messageIndexHeader]: sources,
        });
      }
    },
    onError: (e) => {
      toast.error(`Error while processing your request`, {
        description: e.message,
      });
    },
    onFinish: (message) => {
      // Message finished
      if (props.onMessageFinished) {
        props.onMessageFinished();
      }
    },
  });

  useEffect(() => {
    if (props.initialMessages && props.initialMessages.length > 0 && chat.messages.length === 0) {
      chat.setMessages(props.initialMessages);
    }
  }, [props.initialMessages]);

  // Clear messages and update conversation ID when switching conversations
  useEffect(() => {
    if (props.conversationId !== conversationId) {
      // Clear existing messages when switching conversations
      if (conversationId && props.conversationId !== conversationId) {
        chat.setMessages([]);
      }
      
      setConversationId(props.conversationId);
    }
  }, [props.conversationId, conversationId]);

  // Load initial messages when conversation ID is set and we have initial messages
  useEffect(() => {
    if (props.conversationId && props.initialMessages && props.initialMessages.length > 0) {
      chat.setMessages(props.initialMessages);
    } else if (!props.conversationId) {
      // Clear messages when starting a new conversation (no conversationId)
      chat.setMessages([]);
    }
  }, [props.conversationId, props.initialMessages]);

  // Handle knowledge sources selection
  const handleKnowledgeSourcesSelect = (sourceIds: string[]) => {
    setSelectedKnowledgeSources(sourceIds);
  };

  async function sendMessage(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (chat.isLoading || intermediateStepsLoading) {
      return;
    }

    if (!chat.input.trim()) {
      return;
    }

    const currentConversationId = conversationId;

    // Simplify tool handling - use selectedMCPTool directly
    let mcpTools: MCPToolExtended[] = [];
    
    if (selectedMCPTool) {
      // Handle case where we have multiple selected tools
      if (selectedMCPTool.hasOwnProperty('isMultiToolSelection') && (selectedMCPTool as any).selectedTools) {
        mcpTools = (selectedMCPTool as any).selectedTools;
      } else {
        // Single tool case
        mcpTools = [selectedMCPTool];
      }
    }
    
    // Define the base payload with the tools and knowledge sources
    const basePayload = {
      show_suggestions: showSuggestions,
      show_intermediate_steps: false, // False for useChat (streaming), true for direct fetch (JSON)
      conversationId: currentConversationId,
      mcpTools: mcpTools,  // Pass the complete tools directly
      knowledgeSources: selectedKnowledgeSources.length > 0 ? selectedKnowledgeSources : undefined,
      organizationId: props.organizationId,
      publicScope: props.publicScope,
      isAuthenticated: props.isAuthenticated,
    };
    
    // Handle non-intermediate steps case (normal chat)
    if (!showIntermediateSteps) {
      const options: any = {
        data: { ...basePayload },
      };
      
      try {
        await chat.handleSubmit(e, options as ChatRequestOptions);
      } catch (error) {
        console.error("ChatWindow: Error calling chat.handleSubmit:", error);
      }
      return;
    } 
    
    // Handle intermediate steps
    else {
      setIntermediateStepsLoading(true);
      chat.setInput("");
      const messagesWithUserReply = [...chat.messages, {
        id: chat.messages.length.toString(),
        content: chat.input,
        role: "user",
      } as Message];
      chat.setMessages(messagesWithUserReply);
      
      const requestBody = {
        messages: messagesWithUserReply,
        ...basePayload,
        show_intermediate_steps: true // True for direct fetch (JSON)
      };
      
      try {
        const response = await fetch(props.endpoint, {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json"
          }
        });
        
        const json = await response.json();
        setIntermediateStepsLoading(false);

        if (!response.ok) {
          toast.error(`Error while processing your request`, {
            description: json.error || "Unknown error",
          });
          return;
        }

        const responseMessages = json.messages as ExtendedMessage[];
        const toolCallMessages = responseMessages.filter(
          (responseMessage) => 
            (responseMessage.role === "assistant" && !!responseMessage.tool_calls?.length) ||
            responseMessage.role === "tool"
        );

        const intermediateStepMessages = [];
        for (let i = 0; i < toolCallMessages.length; i += 2) {
          const aiMessage = toolCallMessages[i];
          const toolMessage = toolCallMessages[i + 1];
          if (aiMessage && toolMessage) {
            intermediateStepMessages.push({
              id: (messagesWithUserReply.length + i / 2).toString(),
              role: "system" as const,
              content: JSON.stringify({
                action: aiMessage.tool_calls?.[0],
                observation: toolMessage.content,
              }),
            });
          }
        }
        
        const newMessages = [...messagesWithUserReply];
        for (const message of intermediateStepMessages) {
          newMessages.push(message);
          chat.setMessages([...newMessages]);
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 + Math.random() * 1000),
          );
        }

        // Add the final assistant message
        if (responseMessages.length > 0) {
          chat.setMessages([
            ...newMessages,
            {
              id: newMessages.length.toString(),
              content: responseMessages[responseMessages.length - 1].content,
              role: "assistant",
            },
          ]);
        }
      } catch (error) {
        setIntermediateStepsLoading(false);
        console.error("Error processing intermediate steps:", error);
        toast.error("Error processing your request", {
          description: (error as Error).message || "Unknown error occurred"
        });
      }
    }

    try {
      // Trigger sidebar refresh after message is sent
      setTimeout(() => {
        window.dispatchEvent(new Event('refresh-conversation-sidebar'));
      }, 500);
    } catch (error) {
      console.error("Error triggering sidebar refresh:", error);
    }
  }

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    toast.success("Document uploaded successfully", {
      description:
        "Your document has been processed and is ready for querying.",
      icon: <Sparkles className="h-4 w-4" />,
    });
  };

  const handleSuggestionClick = (text: string) => {
    if (chat && typeof chat.setInput === "function") {
      chat.setInput(text);
    }
  };

  useEffect(() => {
    const handleChartSuggestion = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.query) {
        console.log("Chart suggestion received:", customEvent.detail.query);
        handleSuggestionClick(customEvent.detail.query);

        toast.info("Chart suggestion applied", {
          description: "You can send this query or modify it first.",
          duration: 3000,
        });
      }
    };

    document.addEventListener("ai-chart-suggestion", handleChartSuggestion);

    return () => {
      document.removeEventListener(
        "ai-chart-suggestion",
        handleChartSuggestion,
      );
    };
  }, [chat]);

  useEffect(() => {
    const handleTableSuggestion = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.query) {
        console.log("Table suggestion received:", customEvent.detail.query);
        handleSuggestionClick(customEvent.detail.query);

        toast.info("Table suggestion applied", {
          description: "You can send this query or modify it first.",
          duration: 3000,
        });
      }
    };

    document.addEventListener("ai-table-suggestion", handleTableSuggestion);
    document.addEventListener("ai-suggestion-click", handleTableSuggestion);
    document.addEventListener("table-suggestion-click", handleTableSuggestion);

    return () => {
      document.removeEventListener("ai-table-suggestion", handleTableSuggestion);
      document.removeEventListener("ai-suggestion-click", handleTableSuggestion);
      document.removeEventListener("table-suggestion-click", handleTableSuggestion);
    };
  }, [chat]);

  return (
    <div className="flex flex-col h-full relative">
      <ChatMessages
        aiEmoji={props.emoji}
        messages={chat.messages}
        emptyStateComponent={props.emptyStateComponent ? props.emptyStateComponent : 
          <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center relative">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/10 rounded-2xl"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(120,119,198,0.05),transparent_70%)]"></div>
          
          {/* Content */}
          <div className="relative z-10 max-w-2xl mx-auto">
            {/* Logo Section */}
            <div className="mb-8">
              <div className="inline-flex w-auto px-12 items-center justify-center w-16 h-16 bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-sm border border-border/50 rounded-2xl mb-6 group hover:scale-105 transition-all duration-300">
                <img 
                  className="dark:invert transition-transform group-hover:scale-110" 
                  src="/images/logos/logo.png" 
                  alt="Zynex Logo" 
                  width={28} 
                  height={28} 
                />
              </div>
              
              {/* Badge */}
             
            </div>
        
            {/* Main Content */}
            <div className="space-y-6">
              <TextGenerateEffect 
                words="Start a new conversation" 
                className="text-xl md:text-2xl font-medium tracking-tight text-foreground/90 -mb-5"
              />
              
              <div className="max-w-lg mx-auto">
                <FlipWords 
                  words={[
                    "Create visualizations for your knowledgebase data",
                    "Use MCP to connect to your data sources",
                    "Perform complex calculations and transformations", 
                    "Next generation AI agent"
                  ]} 
                  className="text-md text-muted-foreground/95 font-normal leading-relaxed"
                />
              </div>
            </div>
        
            {/* Action Hints */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg text-xs text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full"></div>
                Type your message below
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg text-xs text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full"></div>
                Add knowledge sources
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg text-xs text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full"></div>
                Connect data sources using MCP
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg text-xs text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full"></div>
                Deploy the agent on your own website
              </div>
            </div>
            </div>
          </div>
        }
        sourcesForMessages={sourcesForMessages}
        isLoading={chat.isLoading || intermediateStepsLoading}
      />

      <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-1 pb-4 z-10 shadow-[0_-8px_16px_-6px_rgba(0,0,0,0.1)] dark:shadow-[0_-8px_16px_-6px_rgba(0,0,0,0.25)]">
        
        <VercelV0Chat
          value={chat.input}
          onChange={(e) => chat.setInput(e.target.value)}
          onSubmit={(e) => {
            sendMessage(e);
          }}
          onStop={() => chat.stop()}
          loading={chat.isLoading || intermediateStepsLoading}
          placeholder={props.placeholder ?? "Ask me anything..."}
          showIngestForm={props.showIngestForm}
          showIntermediateStepsToggle={props.showIntermediateStepsToggle}
          intermediateSteps={showIntermediateSteps}
          setIntermediateSteps={setShowIntermediateSteps}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          selectedMCPTool={selectedMCPTool}
          onMCPToolSelect={(tool) => {
            setSelectedMCPTool(tool);
            if (tool) {
              toast.success(`Using MCP tool: ${tool.name}`);
            }
          }}
          onKnowledgeSourcesSelect={handleKnowledgeSourcesSelect}
          selectedKnowledgeSources={selectedKnowledgeSources}

          
          isAuthenticated={props.isAuthenticated || true}
          organizationId={props.organizationId}
          publicScope={props.publicScope || true}
          baseUrl={props.baseUrl}
          inputClassName={props.inputClassName}
          buttonsClassName={props.buttonsClassName}
          dropdownClassName={props.dropdownClassName}
          dropdownDirection={props.dropdownDirection}
          renderExtraButton={props.showIngestForm ? () => (
            <button
              type="button"
              className="p-2 hover:bg-accent rounded-lg transition-colors flex items-center gap-1 group"
              onClick={() => setUploadDialogOpen(true)}
            >
              <Paperclip className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              <span className="text-xs text-muted-foreground hidden group-hover:inline transition-all">
                Upload
              </span>
            </button>
          ) : undefined}
        />
      </div>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload a document to use as context for the AI agent.
            </DialogDescription>
          </DialogHeader>
          <UploadDocumentsForm onSuccess={handleUploadSuccess} />
        </DialogContent>
      </Dialog>

      <MCPUrlDialog 
        open={uploadDialogOpen} 
        onOpenChange={setUploadDialogOpen}
        onSubmit={(urls) => {
          setSelectedMCPTool(null); // Clear selected tool when using direct URLs
          
          // Save to localStorage for future sessions
          localStorage.setItem('lastConnectedMcpServers', JSON.stringify(urls));
          
          toast.success(`Connected to ${urls.length} MCP server${urls.length > 1 ? 's' : ''}`);
        }}
      />
    </div>
  );
}
